import React from 'react';
import { observer, inject } from 'mobx-react';
import { observable, action, toJS } from 'mobx';
import { Intent } from '@blueprintjs/core';
import moment from 'moment';
import Toast from 'helpers/toaster';
import { CurrentEnv } from 'services/Api';
import { currency } from 'helpers/currency';
import TechnicianPicker from 'components/TechnicianPicker';
import DateInput from 'components/DateInput';
import { Toggle } from 'components/FormBits';
import PdfViewer from 'components/Quote/PdfViewer';
import SendQuoteModal from 'components/Payment/SendQuoteModal';
import UpdateClientModal from 'components/Quote/UpdateClientModal';
import SelectScopeOfWorkModal from 'components/Quote/SelectScopeOfWorkModal';
import AddProductForEstimateModal from 'components/Quote/AddProductForEstimateModal';
import DocModel, { reason } from 'components/DocEditor/DocModel';
import LineItems from 'components/DocEditor/LineItems';
import RichText from 'components/DocEditor/RichText';
import { documentTotals } from 'components/DocEditor/lineMath';
import {
  Toolbar, ToolButton, MoreMenu, StatusSelect, Letterhead, DocTitle, ClientBlock, Meta, LiveTotals,
  SheetSection, SideCard, Signatures, FilesCard, SaveBar, SidePanel, MarkupNote, BarFigures, Breakdown, NotesTabs, money,
} from 'components/DocEditor/DocParts';

// An estimate (pages/quotes/show.js), edited as the proposal the customer
// gets: the scope of work, the priced lines (with markup and tax), the total,
// notes and the agreement, stamped with its status. What the customer sees
// (flat rate, signature, agreement) and your private notes sit beside it, off
// the paper. Same editor as work orders and invoices (components/DocEditor).

export const ESTIMATE_STATUSES = [
  { value: 'draft', label: 'Draft', tone: 'grey' },
  { value: 'submitted', label: 'Submitted', tone: 'purple' },
  { value: 'sent', label: 'Sent', tone: 'blue' },
  { value: 'approved', label: 'Approved', tone: 'green' },
  { value: 'on_hold', label: 'On hold', tone: 'amber' },
  { value: 'rejected', label: 'Rejected', tone: 'red' },
];

const stripHtml = (html) => String(html || '').replace(/(<([^>]+)>)/gi, '').replace(/&nbsp;/g, '');

@inject('store', 'viewStore', 'quoteStore', 'taxStore', 'jobStore') @observer
class EstimateEditor extends React.Component {
  @observable sendOpen = false;
  @observable pickerType = null;
  @observable templatesOpen = false;
  @observable pdf = null; // { url, title }
  @observable busy = null;

  constructor(props) {
    super(props);
    const { quoteStore, viewStore, taxStore } = props;
    const store = quoteStore;
    const id = () => store.quote.object.id;
    this.doc = new DocModel({
      kind: 'estimate',
      noun: 'estimate',
      features: { markup: true, override: false, materialOrders: false, taxes: true },
      fields: ['po_number', 'created_at', 'flat_rate', 'proposal_markup', 'description', 'agreement_copy', 'private_notes', 'enable_invoice_signature', 'hide_agreement'],
      getRecord: () => store.quote.object,
      saveRecord: () => store.update(),
      reloadRecord: () => store.loadQuote({ uuid: id() }),
      truckCharge: () => viewStore.currentUser && viewStore.currentUser.attributes && viewStore.currentUser.attributes.truck_charge,
    });
    Object.assign(this.doc, {
      deleteLine: (item) => store.deleteLineItem(item),
      addBlankLine: (kind) => store.addBlankLineItem(kind, id()),
      openPicker: (type) => { this.pickerType = type; },
      taxes: () => (taxStore.taxes && taxStore.taxes.objects) || [],
      setTax: (taxLine, tax) => taxStore.setTaxLineItem(taxLine, tax),
      // Material taxes couldn't be taken off an estimate before either.
      canDeleteTax: (kind) => kind === 'labour' || kind === 'custom',
      deleteTax: (taxLine, kind) => (kind === 'labour' ? store.deleteLabourTaxLineItem(taxLine) : store.deleteCustomTaxLineItem(taxLine)),
      uploadFiles: (files) => store.uploadWorkReportAttachment(files, id()).then(() => store.update()),
      removeFile: (file) => store.removeAttachment({ id: file.id }, id()).then(() => store.update()),
    });
  }

  componentWillUnmount() {
    this.doc.dispose();
  }

  get record() {
    return this.props.quoteStore.quote.object;
  }

  get attributes() {
    return this.record.attributes;
  }

  pdfUrl(kind) {
    const { currentUser } = this.props.viewStore;
    const path = { costSheet: 'download_pdf', proposal: 'download_proposal', estimate: 'download_estimate' }[kind];
    return `${CurrentEnv.baseURL()}/work_reports/${this.attributes.id}/${path}?api_token=${currentUser.attributes.api_token}&x_tenant_id=${currentUser.attributes.tenant_id}`;
  }

  // Sending and the PDFs use the saved estimate: save changes first.
  afterSave(name, next) {
    this.run(name, () => this.doc.withSaved(() => undefined), () => next(), "Couldn't save your changes. Try again.");
  }

  openPdf(kind, label) {
    this.afterSave('view', () => {
      const a = this.attributes;
      const title = a.job && a.job.id ? `${label}: job #${a.job.event_number}, estimate ${a.number}` : `${label}: estimate ${a.number}`;
      this.pdf = { url: this.pdfUrl(kind), title };
    });
  }

  downloadPdf(kind) {
    this.afterSave('download', () => {
      const opened = window.open(this.pdfUrl(kind));
      if (!opened) Toast.show({ message: 'Your browser blocked the download. Click Download again.', intent: Intent.WARNING });
    });
  }

  run(name, work, success, failure) {
    this.busy = name;
    return work()
      .then((response) => { if (success) success(response); return response; })
      .catch((error) => { Toast.show({ message: reason(error, failure), intent: Intent.DANGER }); })
      .finally(action(() => { this.busy = null; }));
  }

  createProject = () => {
    const { store } = this.props;
    store.projectDetail = this.attributes;
    store.isOpenAddProjectDialog = true;
  };

  changeStatus = (value) => {
    const { quoteStore } = this.props;
    const status = ESTIMATE_STATUSES.find((option) => option.value === value);
    this.run('status',
      () => this.doc.withSaved(() => quoteStore.changeStatus(this.record, value)),
      () => {
        Toast.show({
          message: `Marked as ${status ? status.label : value}.`,
          intent: Intent.SUCCESS,
          // Approving used to open a new project by itself; now it's offered.
          action: value === 'approved' ? { text: 'Create project', onClick: this.createProject } : undefined,
        });
      },
      "Couldn't change the status. Try again.");
  };

  changeTechnician = (selections) => {
    const { quoteStore } = this.props;
    const current = this.attributes.created_by && this.attributes.created_by.id;
    const next = selections.map((option) => option.value).find((value) => value !== current);
    if (!next) return;
    this.run('technician',
      () => this.doc.withSaved(() => quoteStore.changeTechnician(this.record, next)),
      (response) => {
        const person = response && response.data && response.data.data.attributes.created_by;
        Toast.show({ message: person ? `Prepared by changed to ${person.full_name}.` : 'Prepared by changed.', intent: Intent.SUCCESS });
      },
      "Couldn't change who prepared it. Try again.");
  };

  generateInvoice = () => {
    const { quoteStore } = this.props;
    this.run('invoice',
      () => this.doc.withSaved(() => quoteStore.generateInvoice(this.record)),
      () => { Toast.show({ message: 'Invoice created.', intent: Intent.SUCCESS }); },
      "Couldn't create the invoice. Try again.");
  };

  copyLink = () => {
    const link = this.attributes.public_link;
    const clipboard = typeof navigator !== 'undefined' && navigator.clipboard;
    if (!link) return;
    if (!clipboard || !clipboard.writeText) {
      Toast.show({ message: "Couldn't copy. Open the customer's page and copy its address.", intent: Intent.WARNING });
      return;
    }
    clipboard.writeText(link)
      .then(() => { Toast.show({ message: 'Link copied.', intent: Intent.SUCCESS }); })
      .catch(() => { Toast.show({ message: "Couldn't copy. Open the customer's page and copy its address.", intent: Intent.WARNING }); });
  };

  // Dispatch: a new job for this estimate, filled in from it (as before).
  dispatch = () => {
    const { store, jobStore } = this.props;
    const a = this.attributes;
    const job = jobStore.persistJob.object;
    if (a.proposal_markup != null) {
      job.reason_for_call = stripHtml(a.proposal_markup);
      job.title = stripHtml(a.proposal_markup);
    }
    if (a.flat_rate) {
      job.reason_for_call = `Grand Total (${currency()}): ${money(documentTotals(a).grand)}`;
    }
    job.client = { attributes: { ...a.client, name: a.client && a.client.name } };
    job.client_id = a.client && a.client.id;
    job.location = { attributes: a.location };
    job.location_id = a.location && a.location.id;
    job.work_report_ids = [a.id];
    store.isOpen = true;
  };

  renderToolbar() {
    const a = this.attributes;
    const approved = a.status === 'approved';
    return (
      <Toolbar
        actions={(
          <>
            <MoreMenu
              items={[
                a.public_link && { text: "Open the customer's page", icon: 'share', href: a.public_link },
                a.public_link && { text: 'Copy link', icon: 'link', onClick: this.copyLink },
                { text: 'Create invoice', icon: 'document-share', onClick: this.generateInvoice, disabled: this.busy === 'invoice' },
                approved && { text: 'Create project', icon: 'projects', onClick: this.createProject },
                { divider: true, title: 'View PDF' },
                { text: 'Estimate', icon: 'eye-open', onClick: () => this.openPdf('estimate', 'Estimate') },
                { text: 'Proposal', icon: 'eye-open', onClick: () => this.openPdf('proposal', 'Proposal') },
                { text: 'Cost sheet', icon: 'eye-open', onClick: () => this.openPdf('costSheet', 'Cost sheet') },
                { divider: true, title: 'Download PDF' },
                { text: 'Estimate ', icon: 'download', onClick: () => this.downloadPdf('estimate') },
                { text: 'Proposal ', icon: 'download', onClick: () => this.downloadPdf('proposal') },
                { text: 'Cost sheet ', icon: 'download', onClick: () => this.downloadPdf('costSheet') },
              ]}
            />
            <ToolButton icon="eye-open" busy={this.busy === 'view'} onClick={() => this.openPdf('estimate', 'Estimate')}>View PDF</ToolButton>
            {approved && <ToolButton icon="person" onClick={this.dispatch}>Dispatch technician</ToolButton>}
            <ToolButton icon="envelope" primary busy={this.busy === 'send'} onClick={() => this.afterSave('send', () => { this.sendOpen = true; })}>Send estimate</ToolButton>
          </>
        )}
      >
        <StatusSelect value={a.status} statuses={ESTIMATE_STATUSES} disabled={this.busy === 'status'} onChange={this.changeStatus} />
      </Toolbar>
    );
  }

  renderParty() {
    const { viewStore, store } = this.props;
    const a = this.attributes;
    const client = a.client || {};
    const address = (a.current_location && a.current_location.full_address) || (a.location && a.location.full_address);
    const job = a.job && a.job.number ? a.job : null;
    const tech = a.created_by;
    return (
      <div className="de-parties">
        <ClientBlock label="Prepared for" client={client} address={address} href={client.id ? `/dashboard/clients/${client.id}/` : undefined}>
          {!job && (
            <button type="button" className="de-link-button de-party__change" onClick={() => { store.isOpenUpdateClientDialog = true; }}>
              Change client
            </button>
          )}
        </ClientBlock>
        <div className="de-meta">
          <Meta label="Estimate date">
            <div className="de-date">
              <DateInput
                placeholder="Estimate date"
                defaultValue={moment(a.created_at)}
                onChange={(date) => { a.created_at = moment(date); }}
              />
            </div>
          </Meta>
          <Meta label="Prepared by">
            <div className="de-picker">
              <TechnicianPicker
                fill="true"
                selectedOptions={tech ? toJS([{ id: tech.id, label: tech.full_name, value: tech.id, selected: true }]) : []}
                onChange={this.changeTechnician}
                includeAdmins={true}
                hideHeading={true}
              />
            </div>
          </Meta>
          {job && (
            <Meta label="Job">
              <a className="de-strong-link" href={`/dashboard/jobs/${job.id}/${(viewStore.jobTab && viewStore.jobTab.section) || 'details'}`}>#{job.number}</a>
              {job.title && <span className="de-muted">{job.title}</span>}
              {job.service_type_name && <span className="de-muted">{job.service_type_name}</span>}
            </Meta>
          )}
          <Meta label="PO number" htmlFor="de-po">
            <input id="de-po" className="de-field" placeholder="None" value={a.po_number || ''} onChange={(event) => { a.po_number = event.target.value; }} />
          </Meta>
        </div>
      </div>
    );
  }

  renderCustomerOptions() {
    const a = this.attributes;
    return (
      <SideCard title="Customer view">
        <div className="fd de-options">
          <Toggle
            id="de-flat-rate"
            label="Total only (flat rate)"
            hint="The customer sees the total, not each line."
            checked={Boolean(a.flat_rate)}
            onChange={(checked) => { a.flat_rate = checked; }}
          />
          <Toggle
            id="de-signature"
            label="Ask for a signature"
            hint="They sign the agreement when they approve."
            checked={Boolean(a.enable_invoice_signature)}
            onChange={(checked) => { a.enable_invoice_signature = checked; }}
          />
          <Toggle
            id="de-hide-agreement"
            label="Hide the agreement"
            hint="Leave the agreement off their page."
            checked={Boolean(a.hide_agreement)}
            onChange={(checked) => { a.hide_agreement = checked; }}
          />
        </div>
      </SideCard>
    );
  }

  renderEquipment() {
    const equipment = this.attributes.equipments || [];
    if (equipment.length === 0) return null;
    return (
      <SideCard title="Equipment" count={equipment.length}>
        <ul className="de-equipment">
          {equipment.map((unit, index) => (
            <li key={unit.id || index} className="de-equipment__item de-equipment__item--plain">
              <div className="de-equipment__text">
                <strong>{[unit.make, unit.model].filter(Boolean).join(' ') || unit.description || 'Equipment'}</strong>
                <span>{[unit.serial_number && `Serial ${unit.serial_number}`, unit.physical_location].filter(Boolean).join(', ')}</span>
                {unit.description && <span>{unit.description}</span>}
              </div>
            </li>
          ))}
        </ul>
      </SideCard>
    );
  }

  render() {
    const { store } = this.props;
    const doc = this.doc;
    const a = this.attributes;
    const status = ESTIMATE_STATUSES.find((option) => option.value === a.status);
    const accepted = a.is_accepted && a.accepted_at
      ? `, approved ${moment(a.accepted_at).format('MMM D, YYYY [at] h:mm A')}${a.accepted_ip ? ` from ${a.accepted_ip}` : ''}`
      : '';
    return (
      <div className="de de--estimate">
        {this.renderToolbar()}

        <article className="de-sheet" aria-labelledby="de-title">
          <Letterhead tenant={a.tenant} />
          <DocTitle kind="Estimate" number={a.number} stamp={status} />
          {this.renderParty()}

          <SheetSection
            title="Scope of work"
            className="de-section--intro"
            aside={<button type="button" className="de-link-button" onClick={() => { this.templatesOpen = true; }}>Use a template</button>}
          >
            <RichText label="Scope of work" value={a.proposal_markup || ''} placeholder="What you'll do, in the customer's words" onChange={(html) => { a.proposal_markup = html; }} />
          </SheetSection>

          <LineItems doc={doc} />

          <LiveTotals
            doc={doc}
            build={(totals) => ({
              note: <><Breakdown doc={doc} /><MarkupNote totals={totals} /></>,
              rows: [
                { label: 'Subtotal', value: totals.subtotal },
                { label: 'Tax', value: totals.tax },
                { label: 'Total', value: totals.grand, strong: true },
              ],
            })}
          />

          <NotesTabs
            kind="estimate"
            attributes={a}
            RichTextEditor={RichText}
            tabs={[
              { field: 'description', label: 'Notes for the customer', note: 'Shown to the customer on the estimate', placeholder: "Anything they should know: timing, what's included, how to prepare" },
              {
                field: 'agreement_copy',
                label: 'Agreement',
                placeholder: 'Terms the customer agrees to when they approve',
                hidden: Boolean(a.hide_agreement),
                note: a.hide_agreement ? 'Hidden from the customer (change it in Customer view)' : 'Shown to the customer when they approve',
              },
              { field: 'private_notes', label: 'Private notes', private: true, placeholder: "Notes for your team: access codes, follow-ups, anything the customer shouldn't see" },
            ]}
          />

          <SheetSection title="Signatures" className="de-section--signatures">
            <Signatures signatures={a.signatures} accepted={accepted} />
          </SheetSection>
        </article>

        <SidePanel kind="estimate">
          {this.renderCustomerOptions()}
          <FilesCard title="Files" count={(a.images || []).filter((file) => file && file.asset && file.asset.url).length} doc={doc} images={a.images} />
          {this.renderEquipment()}
        </SidePanel>

        <SaveBar
          doc={doc}
          figures={(
            <BarFigures
              doc={doc}
              build={(totals) => [
                { label: 'Gross markup', text: totals.markupPercent === null ? '–' : `${totals.markupPercent}%` },
                { label: 'Gross margin', value: totals.margin },
                { label: 'Tax', value: totals.tax },
                { label: 'Total', value: totals.grand, strong: true },
              ]}
            />
          )}
        />

        <SendQuoteModal isOpen={this.sendOpen} onClose={() => { this.sendOpen = false; }} onSuccess={() => { this.sendOpen = false; }} />
        {this.pdf && <PdfViewer isOpen url={this.pdf.url} title={this.pdf.title} onClose={() => { this.pdf = null; }} />}
        <UpdateClientModal isOpen={store.isOpenUpdateClientDialog} onClose={() => { store.isOpenUpdateClientDialog = false; }} />
        <SelectScopeOfWorkModal
          isOpen={this.templatesOpen}
          warningRequired={Boolean(stripHtml(a.proposal_markup).trim())}
          onClose={() => { this.templatesOpen = false; }}
          onSuccess={(html) => { a.proposal_markup = html; this.templatesOpen = false; }}
        />
        <AddProductForEstimateModal
          isOpen={Boolean(this.pickerType)}
          type={this.pickerType || 'Service'}
          itemType={this.pickerType === 'Service' ? 'Labor' : 'Material'}
          workDetail={this.record}
          onClose={() => { this.pickerType = null; }}
        />
      </div>
    );
  }
}

export default EstimateEditor;
